import { useEffect, useState } from 'react'
import { Dumbbell } from 'lucide-react'
import Modal from '../../../../components/ERP/Modal/Modal'
import { patientService } from '../../../../services/patientApi'
import { ACTIVITY_STATUS } from './therapyFormat'

const EMPTY = {
  title: '',
  frequency: '',
  sets: '',
  repetitions: '',
  guidance: '',
  mediaUrl: '',
  status: 'ativa'
}

function fromActivity(activity) {
  if (!activity) return EMPTY
  return {
    title: activity.title || '',
    frequency: activity.frequency || '',
    sets: activity.sets ?? '',
    repetitions: activity.repetitions ?? '',
    guidance: activity.guidance || '',
    mediaUrl: activity.media_url || '',
    status: activity.status || 'ativa'
  }
}

function TherapyActivityModal({ isOpen, patientId, activity, onClose, onSaved }) {
  const [form, setForm] = useState(EMPTY)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    if (!isOpen) return
    setForm(fromActivity(activity))
    setError('')
  }, [isOpen, activity])

  const set = (key) => (event) => setForm((current) => ({ ...current, [key]: event.target.value }))

  const submit = async (event) => {
    event.preventDefault()
    if (!form.title.trim()) {
      setError('Informe o nome da atividade.')
      return
    }
    const mediaUrl = form.mediaUrl.trim()
    if (mediaUrl && !/^https?:\/\/\S+$/i.test(mediaUrl)) {
      setError('O material de apoio precisa ser um link http ou https.')
      return
    }
    setSaving(true)
    setError('')
    const payload = {
      title: form.title.trim(),
      frequency: form.frequency.trim(),
      sets: form.sets === '' ? null : Number(form.sets),
      repetitions: form.repetitions === '' ? null : Number(form.repetitions),
      guidance: form.guidance.trim(),
      mediaUrl,
      status: form.status
    }
    try {
      if (activity?.id) await patientService.updateTherapyActivity(patientId, activity.id, payload)
      else await patientService.addTherapyActivity(patientId, payload)
      onSaved()
      onClose()
    } catch (err) {
      setError(err.response?.data?.message || 'Não foi possível salvar a atividade')
    } finally {
      setSaving(false)
    }
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={activity ? 'Editar atividade' : 'Nova atividade'}
      subtitle="Plano de reabilitação"
      icon={<Dumbbell size={22} />}
      size="large"
      footer={(
        <button type="submit" form="therapy-activity-form" disabled={saving}>
          {saving ? 'Salvando...' : 'Salvar atividade'}
        </button>
      )}
    >
      <form id="therapy-activity-form" onSubmit={submit}>
        {error && <p className="patients-alert-strip">{error}</p>}
        <div className="patients-form-grid patients-form-grid--2">
          <div className="patients-span-2">
            <label htmlFor="therapy-activity-title">Atividade *</label>
            <input id="therapy-activity-title" value={form.title} onChange={set('title')} placeholder="Ex.: Ponte de glúteo" maxLength={180} />
          </div>
          <div>
            <label htmlFor="therapy-activity-frequency">Frequência</label>
            <input id="therapy-activity-frequency" value={form.frequency} onChange={set('frequency')} placeholder="Ex.: 2x ao dia" maxLength={120} />
          </div>
          {activity && (
            <div>
              <label htmlFor="therapy-activity-status">Status</label>
              <select id="therapy-activity-status" value={form.status} onChange={set('status')}>
                {Object.entries(ACTIVITY_STATUS).map(([value, label]) => (
                  <option key={value} value={value}>{label}</option>
                ))}
              </select>
            </div>
          )}
          <div>
            <label htmlFor="therapy-activity-sets">Séries</label>
            <input id="therapy-activity-sets" type="number" min="0" max="999" step="1" value={form.sets} onChange={set('sets')} />
          </div>
          <div>
            <label htmlFor="therapy-activity-reps">Repetições</label>
            <input id="therapy-activity-reps" type="number" min="0" max="999" step="1" value={form.repetitions} onChange={set('repetitions')} />
          </div>
          <div className="patients-span-2">
            <label htmlFor="therapy-activity-guidance">Orientações</label>
            <textarea id="therapy-activity-guidance" rows={3} value={form.guidance} onChange={set('guidance')} placeholder="Como executar, cuidados e progressão" />
          </div>
          <div className="patients-span-2">
            <label htmlFor="therapy-activity-media">Material de apoio</label>
            <input id="therapy-activity-media" type="url" value={form.mediaUrl} onChange={set('mediaUrl')} placeholder="https://www.youtube.com/..." />
            <p className="patients-field-hint">Link opcional de vídeo ou documento para o cuidador.</p>
          </div>
        </div>
      </form>
    </Modal>
  )
}

export default TherapyActivityModal
